import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import SaveActionButton from 'components/SaveActionButton';
import { FormApi } from 'final-form';
import moment from 'moment';
import React, { Component } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, SemanticWIDTHS, Table } from 'semantic-ui-react';
import { PrescriptionMedicament, PrescriptionMedicamentDto } from 'ts-types/api.types';

const addButton = {
  marginBottom: 10,
  marginTop: 10
};

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  field: string,
  fields: any,
  prescriptionIx: number,
  savePrescription: () => void,
  cancelEditPrescription: (fields: any, index: number) => void,
  medicaments: Array<PrescriptionMedicamentDto>,
  prescriptionMedicament?: Array<PrescriptionMedicament>,
  form: FormApi,
  readOnly: boolean
}

interface State {
  editPrescriptionMedicament: boolean,
  editPrescriptionMedicamentIx?: number,
  prescriptionMedicamentCopy?: Partial<PrescriptionMedicament>,
  prescriptionMedicament: Array<Partial<PrescriptionMedicament>>,
  dosageOptions: Array<DropdownOption>,
  errors: Array<boolean>,
  errorMessage?: string
}

class PrescriptionMedicamentsModal extends Component<Props, State> {

  tableWidths: SemanticWIDTHS[] = ["8", "2", "4", "1"];

  constructor(props: Props) {
    super(props);

    const {prescriptionMedicament = [], fields, prescriptionIx} = this.props;

    let editPrescriptionMedicament = false;
    let editPrescriptionMedicamentIx = undefined;
    if (fields.value[prescriptionIx].medicaments.length === 1
        && !fields.value[prescriptionIx].medicaments[0].id) {
      editPrescriptionMedicament = true;
      editPrescriptionMedicamentIx = 0;
    }

    this.state = {
      editPrescriptionMedicament: editPrescriptionMedicament,
      editPrescriptionMedicamentIx: editPrescriptionMedicamentIx,
      prescriptionMedicament: prescriptionMedicament,
      dosageOptions: [],
      errors: [false, false]
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const {fields, prescriptionIx} = this.props;
    const {fields: prevFields} = prevProps;
    const {editPrescriptionMedicamentIx: index, editPrescriptionMedicament} = this.state;

    if (editPrescriptionMedicament && index !== undefined) {
      const prescription = fields.value[prescriptionIx];
      const presMed = prescription.medicaments[index];

      const prevMed = prevFields.value[prescriptionIx].medicaments[index];
      if (presMed
          && presMed.medicamentId
          && (prevMed.medicamentId === undefined
              || prevMed.medicamentId && (prevMed.medicamentId !== presMed.medicamentId))
      ) {
        const dosageOptions = this.getDosagesForMedicament(presMed);
        this.setState({
          dosageOptions: dosageOptions
        });
      }
    }
  };

  validateFields = (prescriptionMedicament: Partial<PrescriptionMedicament>): boolean => {

    let errors: Array<boolean> = [];

    errors[0] = !prescriptionMedicament.medicamentId;
    errors[1] = !(prescriptionMedicament.dosage && prescriptionMedicament.dosage.length > 0);

    this.setState({
      errors: errors
    });

    return errors.includes(true);
  };

  getDosagesForMedicament = (medicament: Partial<PrescriptionMedicament>): Array<DropdownOption> => {
    const {medicaments} = this.props;

    let dosageOptions: Array<DropdownOption> = [];

    const medId = medicament.medicamentId;
    if (medicament.dosage && medicament.dosage.length > 0) {
      const dosage = {
        key: medicament.dosage,
        text: medicament.dosage,
        value: medicament.dosage
      }
      dosageOptions = [dosage];
    }

    const foundMedicament = medicaments.find(med => med.id === medId);
    if (foundMedicament && foundMedicament.dosages && foundMedicament.dosages.length > 0) {
      foundMedicament.dosages.forEach(d => {
        if (!dosageOptions.length || (dosageOptions[0] && dosageOptions[0].value !== d)) {
          const dosage: DropdownOption = {
            key: d,
            text: d,
            value: d
          }
          dosageOptions.push(dosage);
        }
      });
    }

    return dosageOptions;
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  addPrescriptionMedicament = (fields: any, index: number) => {

    const {prescriptionMedicament} = this.state;

    const newPresMed: Partial<PrescriptionMedicament> = {
      medicamentId: undefined,
      dosage: "",
      remarks: ""
    }

    fields.push(newPresMed);

    this.setState({
      editPrescriptionMedicament: true,
      editPrescriptionMedicamentIx: index,
      prescriptionMedicament: prescriptionMedicament ? [...prescriptionMedicament, newPresMed] : [newPresMed]
    })
  };

  savePrescriptionMedicament = (prescriptionMedicament: Partial<PrescriptionMedicament>) => {
    if (!this.validateFields(prescriptionMedicament)) {
      this.setState({
        editPrescriptionMedicament: false,
        editPrescriptionMedicamentIx: undefined,
        dosageOptions: []
      });
    }
  };

  isAfterDate = (value: string) => {

    if (moment(value).isAfter()) {
      const errorString = "Invalid date. The date can't be in the future";
      this.setErrorMessage(errorString)
      return errorString;
    }

    if (this.state.errorMessage && this.state.errorMessage.length > 0) {
      this.setErrorMessage(undefined);
    }

    return undefined;
  }

  cancelEditPrescriptionMedicament = (fields: any, index: number) => {

    const {prescriptionMedicamentCopy} = this.state;

    this.setState({
      editPrescriptionMedicament: false,
      editPrescriptionMedicamentIx: undefined,
      prescriptionMedicamentCopy: undefined,
      dosageOptions: [],
      errors: [false, false, false]
    });

    fields.remove(index);
    if (prescriptionMedicamentCopy) {
      fields.insert(index, prescriptionMedicamentCopy);
    }
  };

  editPrescriptionMedicament = (index: number, field: Partial<PrescriptionMedicament>, fields: any): void => {

    const {editPrescriptionMedicament, editPrescriptionMedicamentIx, prescriptionMedicamentCopy} = this.state;

    if (editPrescriptionMedicament) {

      fields.remove(editPrescriptionMedicamentIx);
      fields.insert(editPrescriptionMedicamentIx, prescriptionMedicamentCopy);

      this.setState({
        editPrescriptionMedicamentIx: index,
        prescriptionMedicamentCopy: field
      });
    } else {

      const dosageOptions = this.getDosagesForMedicament(field);

      this.setState({
        editPrescriptionMedicament: true,
        editPrescriptionMedicamentIx: index,
        prescriptionMedicamentCopy: field,
        dosageOptions: dosageOptions
      })
    }
  };

  deletePrescriptionMedicament = (fields: any, index: number) => {
    const {getMessage} = this.props;
    fields.remove(index);
    if (fields.length <= 1) {
      this.setErrorMessage(getMessage("prescription.noMeds"));
    }
  }

  render(): React.ReactNode {

    const {savePrescription, cancelEditPrescription, field, fields, prescriptionIx, getMessage, readOnly} = this.props;
    const {editPrescriptionMedicament, editPrescriptionMedicamentIx, errorMessage} = this.state;
    const dateFieldValue = fields.value[prescriptionIx].prescriptionDate;

    let disableSave = !fields.value[prescriptionIx].medicaments.length
        || editPrescriptionMedicament
        || moment(dateFieldValue).isAfter();

    return (
        <FormContainer>
          <FormContent>
            {errorMessage
                && (
                    <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                      {errorMessage}
                    </StyledErrorMessage>
                )
            }
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column width={1} verticalAlign="middle">
                  {getMessage("date")}
                </Grid.Column>
                <Grid.Column width={3} verticalAlign="middle">
                  <Field
                      name={`${field}.prescriptionDate`}
                      component={DatePicker}
                      disabledAfter={moment().toDate()}
                      validate={this.isAfterDate}
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <Table celled selectable>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell width={this.tableWidths[0]}>{getMessage('medicament')}</Table.HeaderCell>
                        <Table.HeaderCell width={this.tableWidths[1]}>{getMessage('dosage')}</Table.HeaderCell>
                        <Table.HeaderCell width={this.tableWidths[2]}>{getMessage('remarks')}</Table.HeaderCell>
                        <Table.HeaderCell width={this.tableWidths[3]}>{getMessage('actions')}</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      <FieldArray name={`${field}.medicaments`}>
                        {({fields}) =>
                            fields.map((field, index: number) => {
                              if (fields.value[index] !== undefined) {
                                return (
                                    (editPrescriptionMedicament && editPrescriptionMedicamentIx === index)
                                        ? this.renderPrescriptionMedicamentForm(field, fields.value[index], index, fields)
                                        : this.renderPrescriptionMedicamentTable(fields.value[index], index, fields)
                                )
                              }
                            })
                        }
                      </FieldArray>
                    </Table.Body>
                  </Table>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <FieldArray name={`${field}.medicaments`}>
                    {({fields}) => (
                        <Button
                            type="button"
                            compact
                            style={addButton}
                            onClick={() => this.addPrescriptionMedicament(fields, fields.length!)}
                            disabled={editPrescriptionMedicament || readOnly}
                        >
                          {getMessage('button.add')}
                        </Button>
                    )}
                  </FieldArray>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Field
                      name={`${field}.remark`}
                      component={TextArea}
                      rows={2}
                      placeholder={getMessage("remarks")}
                  />
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
          </FormContent>
          <FormButtonsRow>
            <Button compact type="button" primary
                    onClick={() => savePrescription()}
                    disabled={disableSave}
            >
              {getMessage("button.save")}
            </Button>
            <Button compact type="button"
                    onClick={() => cancelEditPrescription(fields, prescriptionIx)}>
              {getMessage("button.cancel")}
            </Button>
          </FormButtonsRow>
        </FormContainer>
    );
  }

  renderPrescriptionMedicamentTable = (medicament: Partial<PrescriptionMedicament>, index: number, fields: any) => {
    const {medicaments, readOnly, getMessage} = this.props;

    const selectedMedicament = medicaments.find(m => m.id === medicament.medicamentId);
    const medicamentDesc = selectedMedicament
        ? selectedMedicament.description
        : getMessage("prescription.notFound");

    return (
        <React.Fragment key={index}>
          <Table.Row>
            <Table.Cell width={this.tableWidths[0]}>
              {medicamentDesc}
            </Table.Cell>
            <Table.Cell width={this.tableWidths[1]}>
              {medicament.dosage}
            </Table.Cell>
            <Table.Cell width={this.tableWidths[2]}>{medicament.remarks}</Table.Cell>
            <Table.Cell width={this.tableWidths[3]}>
              <EditActionButton
                  hoverMessage={getMessage('edit')}
                  onConfirm={() => this.editPrescriptionMedicament(index, medicament, fields)}
              />
              <DeleteActionButton
                  hoverMessage={getMessage("msgDelete")}
                  onConfirm={() => this.deletePrescriptionMedicament(fields, index)}
                  disabled={readOnly}
              />
            </Table.Cell>
          </Table.Row>
        </React.Fragment>
    );
  };

  renderPrescriptionMedicamentForm = (
      field: string,
      prescriptionMedicament: Partial<PrescriptionMedicament>,
      index: number,
      fields: any
  ) => {

    const {medicaments, getMessage} = this.props;
    const {dosageOptions, errors} = this.state;

    const medicamentOptions = medicaments.map(med => {
      return {
        key: med.id,
        text: med.description,
        value: med.id
      }
    });

    return (
        <Table.Row key={index}>
          <Table.Cell width={this.tableWidths[0]}>
            <Field
                name={`${field}.medicamentId`}
                component={Select}
                options={medicamentOptions}
                search
                selection
                upward
                fluid
                meta={{touched: errors[0], error: errors[0]}}
            />
          </Table.Cell>
          <Table.Cell width={this.tableWidths[1]}>
            <Field
                name={`${field}.dosage`}
                component={Select}
                options={dosageOptions}
                selection
                allowAdditions
                search
                upward
                fluid
                meta={{touched: errors[1], error: errors[1]}}
            />
          </Table.Cell>
          <Table.Cell width={this.tableWidths[2]}>
            <Field
                name={`${field}.remarks`}
                component={Input}
                fluid
            />
          </Table.Cell>
          <Table.Cell width={this.tableWidths[3]}>
            <SaveActionButton hoverMessage={getMessage('button.save')}
                              onConfirm={() => this.savePrescriptionMedicament(prescriptionMedicament)}/>
            <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                onConfirm={() => this.cancelEditPrescriptionMedicament(fields, index)}/>
          </Table.Cell>
        </Table.Row>
    );
  }
}

export default withAuthContext(withMessages(PrescriptionMedicamentsModal));
